<template>
  <v-container max-width="100%" style="margin-top: 2%">
    <v-card class="mb-4" outlined>
      <v-card-title class="d-flex flex-wrap align-center justify-space-between">
        <span class="headline" style="color: #4a4a4a">{{ $t('ticketPorStatus.title') }}</span>
        <v-btn-toggle
          v-model="viewMode"
          mandatory
          dense
          color="primary"
          class="mt-2 mt-sm-0"
        >
          <v-btn value="status" small>
            {{ $t('ticketPorStatus.viewModes.status') }}
          </v-btn>
          <v-btn value="agendamento" small>
            {{ $t('ticketPorStatus.viewModes.agendamento') }}
          </v-btn>
        </v-btn-toggle>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="2">
            <v-autocomplete
              v-model="selectedEmpresa"
              :items="empresas"
              item-text="razao_social"
              item-value="id_empresa"
              :label="$t('ticketStatusAgendamento.filters.empresa')"
              prepend-icon="mdi-domain"
              outlined
              dense
              clearable
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" md="3">
            <MultiplaEscolha
              lazy
              get-url="getUnidadeslazy"
              v-bind:selected-items.sync="selectedUnidades"
              text="descricao_unidade"
              :item-label="unidadeSelectText"
              value="id_unidade"
              :label="$t('nav.registration.units')"
              :max-items="2"
              prepend-icon="mdi-office-building"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="3">
            <MultiplaEscolha
              lazy
              get-url="getAreaslazy"
              v-bind:selected-items.sync="selectedAreas"
              text="nome_area"
              :item-label="areaSelectText"
              value="id_area"
              :request-data="{ zona_risco: 0 }"
              :label="$t('ticket.filters.group.area')"
              :max-items="2"
              prepend-icon="mdi-vector-square"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="3">
            <MultiplaEscolha
              lazy
              get-url="getAgenciasempregolazy"
              v-bind:selected-items.sync="selectedAgencias"
              text="nome"
              value="id_agencia_emprego"
              :label="$t('ticketSolicitacaoCandidatos.fields.agencia')"
              :max-items="2"
              prepend-icon="mdi-account-tie"
              outlined
              dense
            />
          </v-col>
          <!-- Período: sempre na visão por status; na por agendamento só na aba Finalizados. -->
          <template v-if="showPeriodoFilters">
            <v-col cols="12" md="2">
              <v-menu
                v-model="menuStartDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="formatDateBr(dateStart)"
                    :label="$t('datetime.startDate')"
                    prepend-icon="mdi-calendar"
                    readonly
                    outlined
                    dense
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="dateStart = null"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="dateStart"
                  no-title
                  scrollable
                  locale="pt"
                  :max="dateEnd"
                  @input="menuStartDate = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" md="2">
              <v-menu
                v-model="menuEndDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="formatDateBr(dateEnd)"
                    :label="$t('datetime.finalDate')"
                    prepend-icon="mdi-calendar"
                    readonly
                    outlined
                    dense
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="dateEnd = null"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="dateEnd"
                  no-title
                  scrollable
                  locale="pt"
                  :min="dateStart"
                  @input="menuEndDate = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
          </template>
          <v-col cols="12" md="2">
            <v-select
              v-model="selectedSlaFaixa"
              :items="optionsSlaFaixa"
              :label="$t('ticket.filters.resolutionExpected')"
              prepend-icon="mdi-timer-sand"
              outlined
              dense
            ></v-select>
          </v-col>
        </v-row>

        <!-- Cards: visão por status -->
        <v-row v-if="viewMode === 'status'" justify="center" class="flex-wrap">
          <v-col v-for="config in statusConfig" :key="config.id" cols="6" sm="4" md="2">
            <v-card :color="getStatusCardColor(config.id)" outlined>
              <v-card-text>
                <div class="text-subtitle-2">{{ statusLabel(config.id) }}</div>
                <div class="text-h5">{{ countByStatus(config.id) }}</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6" sm="4" md="2">
            <v-card color="blue lighten-3" outlined>
              <v-card-text>
                <div class="text-subtitle-2">{{ $t('ticketPorStatus.finalizedTotal') }}</div>
                <div class="text-h5">{{ contagens.finalizados }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Cards: visão por agendamento -->
        <v-row v-else justify="center" class="flex-wrap">
          <v-col
            v-for="cat in agendamentoCategories"
            :key="'card-' + cat.id"
            cols="6"
            sm="4"
            md="2"
          >
            <v-card :color="cat.cardColor" outlined>
              <v-card-text>
                <div class="text-subtitle-2">{{ $t(cat.labelKey) }}</div>
                <div class="text-h5">{{ countByAgendamento(cat.id) }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="tabs-container">
      <v-tabs v-model="activeTab" background-color="primary" dark show-arrows>
        <template v-if="viewMode === 'status'">
          <v-tab v-for="config in statusConfig" :key="'tab-' + config.id">
            {{ statusLabel(config.id) }} ({{ countByStatus(config.id) }})
          </v-tab>
        </template>
        <template v-else>
          <v-tab v-for="cat in agendamentoCategories" :key="'tab-' + cat.id">
            {{ $t(cat.labelKey) }} ({{ countByAgendamento(cat.id) }})
          </v-tab>
        </template>
      </v-tabs>

      <v-card-text>
        <merus-data-table
          :headers="headersWithProperties"
          :items="tickets"
          :loading="loadingtable"
          :loading-text="$t('system.messages.loading')"
          :server-items-length="totalTickets"
          :items-per-page="numberOfRows"
          :items-per-page-last-option="itemsPerPageLastOption"
          :server-sort-by="serverSortBy"
          :server-sort-desc="serverSortDesc"
          :default-multi-sort="defaultSort"
          cod-tabela="RELTICKETSPORSTATUS"
          item-key="id_ticket"
          class="elevation-7"
          locale="pt"
          dense
          style="border-radius: 10px; margin-bottom: 10px; margin-top: 10px;"
          @update:headers="headers = $event"
          @update:options="onTableOptions"
          @update:filters="onFiltersUpdate"
        >
          <template v-slot:toolbar>
            <CadastroToolbar
              :token="token || ''"
              :title="exportTitle"
              :items="[]"
              :load-items-for-export="loadItemsForExport"
              :export-total="totalTickets"
              :csv-title="exportCsvTitle"
              :pdf-title="$t('ticketPorStatus.title')"
              :pdf-headers="headers"
              :pdf-filename="exportCsvTitle"
              :pdf-subtitle="activeTabLabel"
              hide-search
            />
          </template>

          <template v-slot:item.status_extenso="{ item }">
            <v-chip small :color="getStatusColor(item.id_status_atribuido || item.id_status_dispatcher)" dark>
              {{ item.status_extenso }}
            </v-chip>
          </template>

          <template v-slot:item.dt_agendamento_extenso="{ item }">
            <span v-if="item.dt_agendamento_extenso" :class="{ 'red--text': isOverdue(item) }">
              {{ item.dt_agendamento_extenso }}
            </span>
            <span v-else class="grey--text">{{ $t('ticketStatusAgendamento.noScheduledDate') }}</span>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small color="primary" v-bind="attrs" v-on="on" @click="openTicketDetail(item)">
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('ticketPorStatus.viewDetails') }}</span>
            </v-tooltip>
          </template>

          <template v-slot:no-data>
            <div class="py-6 text-center">
              <v-icon large color="grey">mdi-ticket-outline</v-icon>
              <div class="text-h6 mt-4 grey--text">{{ $t('ticketPorStatus.noTickets') }}</div>
            </div>
          </template>
        </merus-data-table>
      </v-card-text>
    </v-card>

    <ticket-detail-drawer
      v-model="detailOpen"
      :ticket="selectedTicket"
      :ticket-id="selectedTicket && selectedTicket.id_ticket"
      :token="token"
      :user="user"
      :status="allStatusList"
      :visible-detail-fields="visibleDetailFields"
      hide-internal-observation
      read-only
    />
  </v-container>
</template>

<script>
import { rotuloCatalogo } from '@/utils/ticketCatalogoLabel';

import apiController from "@/utils/apiController";
import MerusDataTable from "../Tables/MerusDataTable.vue";
import MultiplaEscolha from "../Selects/MultiplaEscolha.vue";
import CadastroToolbar from "../Toolbars/CadastroToolbar.vue";
import TicketDetailDrawer from "./TicketDetailDrawer.vue";
import { TICKET_SLA_FAIXAS } from "./TicketsViewToolbar.vue";
import serverTableMixin from "@/mixins/serverTableMixin";
import { filterTicketStatusesByPerfil } from "@/utils/ticketStatusPerfilFilter";
import {
  buildRequestParams,
  computePageState,
  initSortFromDefault,
  fetchAllServerPagesForExport,
} from "@/utils/serverTablePagination";

/** Janela padrão do filtro de data (dias até hoje). */
const DIAS_PERIODO_PADRAO = 30;

/** Status fechados — mesma regra do backend / tela antiga de agendamento. */
const STATUS_FECHADOS = [4, 5, 7, 8];

const AGENDAMENTO_CATEGORIES = [
  { id: 'past', labelKey: 'ticketStatusAgendamento.categories.past', cardColor: 'red lighten-4' },
  { id: 'today', labelKey: 'ticketStatusAgendamento.categories.today', cardColor: 'yellow lighten-4' },
  { id: 'future', labelKey: 'ticketStatusAgendamento.categories.future', cardColor: 'green lighten-4' },
  { id: 'unscheduled', labelKey: 'ticketStatusAgendamento.categories.unscheduled', cardColor: 'grey lighten-3' },
  { id: 'finalized', labelKey: 'ticketStatusAgendamento.categories.finalized', cardColor: 'blue lighten-3' },
];

function toPickerDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function diasAtras(dias) {
  const date = new Date();
  date.setDate(date.getDate() - dias);
  return date;
}

function parseLocalDate(raw) {
  if (raw == null || raw === '') return null;
  if (raw instanceof Date) {
    if (isNaN(raw.getTime())) return null;
    return new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
  }
  const s = String(raw).trim();
  const datePart = s.split(/[\sT]/)[0];
  const parts = datePart.split('-').map(Number);
  if (parts.length !== 3 || parts.some((p) => !Number.isFinite(p))) return null;
  return new Date(parts[0], parts[1] - 1, parts[2]);
}

export default {
  mixins: [serverTableMixin],
  components: {
    MerusDataTable,
    MultiplaEscolha,
    CadastroToolbar,
    TicketDetailDrawer,
  },
  data: () => ({
    headers: [],
    user: null,
    token: '',
    tickets: [],
    activeTab: 0,
    /** 'status' | 'agendamento' */
    viewMode: 'status',
    statusList: [],
    allStatusList: [],
    detailOpen: false,
    selectedTicket: null,
    empresas: [],
    selectedEmpresa: null,
    selectedUnidades: [],
    selectedAgencias: [],
    /** Faixa de SLA (10H/17H/24H/48H/ATRASADO); null = todas. */
    selectedSlaFaixa: null,
    selectedAreas: [],
    menuStartDate: false,
    menuEndDate: false,
    dateStart: toPickerDate(diasAtras(DIAS_PERIODO_PADRAO)),
    dateEnd: toPickerDate(new Date()),
    contagens: { porStatus: {}, finalizados: 0, agendamento: {} },
    // Paginação server-side (serverTableMixin)
    loadingtable: false,
    totalTickets: 0,
    pageNumber: 1,
    numberOfRows: 10,
    itemsPerPageLastOption: 1000,
    lastItemsPerPageBeforeTodos: null,
    serverSortBy: [],
    serverSortDesc: [],
    searchFilters: {},
    columnFilters: {},
    /** Evita loop ao sincronizar viewMode com a query da rota. */
    _syncingViewFromRoute: false,
  }),
  computed: {
    agendamentoCategories() {
      return AGENDAMENTO_CATEGORIES;
    },
    /** Status ativos que o perfil do usuário pode ver como coluna (perfis_usuarios.ids_status_tickets_colunas). */
    statusConfig() {
      return this.statusList.map(s => ({ id: Number(s.id_status_ticket), nome: rotuloCatalogo(this, s, 'status_ticket') }));
    },
    activeStatusId() {
      const config = this.statusConfig[this.activeTab] || this.statusConfig[0];
      return config ? config.id : null;
    },
    activeCategoriaAgendamento() {
      const cat = this.agendamentoCategories[this.activeTab] || this.agendamentoCategories[0];
      return cat ? cat.id : null;
    },
    activeTabLabel() {
      if (this.viewMode === 'agendamento') {
        const cat = this.agendamentoCategories[this.activeTab];
        return cat ? this.$t(cat.labelKey) : '';
      }
      return this.statusLabel(this.activeStatusId);
    },
    exportTitle() {
      return `${this.$t('ticketPorStatus.title')} - ${this.activeTabLabel}`;
    },
    exportCsvTitle() {
      if (this.viewMode === 'agendamento') {
        return `ticketsporstatus-agendamento-${this.activeCategoriaAgendamento || 'all'}`;
      }
      return `ticketsporstatus-${this.activeStatusId}`;
    },
    headersWithProperties() {
      return this.headers.map(header => (header.value === 'actions' ? header : {
        ...header,
        filtrable: true,
        sortable: true
      }));
    },
    visibleDetailFields() {
      const list = this.$store && this.$store.state.config && this.$store.state.config.ticketCamposDetalhe;
      if (!list || list.length === 0) return null;
      return this.$store.getters['config/ticketCamposDetalheForScreen']('dispatcher');
    },
    optionsSlaFaixa() {
      return [{ text: this.$t('ticket.filters.resolutionAll'), value: null }].concat(
        TICKET_SLA_FAIXAS.map((faixa) => ({
          text: faixa === 'ATRASADO'
            ? this.$t('ticket.filters.resolutionLate')
            : this.$t('ticket.filters.resolutionOption', { n: parseInt(faixa, 10) }),
          value: faixa,
        }))
      );
    },
    defaultSort() {
      if (this.viewMode === 'agendamento') {
        return [
          { value: 'dt_agendamento_extenso', options: { text: this.$t('system.labels.increasing'), value: 1 } },
        ];
      }
      return [
        { value: 'dt_inclusao_extenso', options: { text: this.$t('system.labels.decreasing'), value: -1 } },
      ];
    },
    /** Período visível: visão status, ou aba Finalizados na visão agendamento. */
    showPeriodoFilters() {
      if (this.viewMode !== 'agendamento') return true;
      return this.activeCategoriaAgendamento === 'finalized';
    },
  },
  created() {
    this.user = JSON.parse(sessionStorage.getItem("user"));
    this.token = sessionStorage.getItem("api") || '';
    this.applyViewFromRoute();
    const sort = initSortFromDefault(this.defaultSort);
    this.serverSortBy = sort.serverSortBy;
    this.serverSortDesc = sort.serverSortDesc;
    this.initialize();
  },
  watch: {
    '$route.query.view'() {
      this.applyViewFromRoute();
    },
    viewMode(next, prev) {
      if (this._syncingViewFromRoute) return;
      if (next === prev) return;
      this.activeTab = 0;
      this.syncViewToRoute(next);
      const sort = initSortFromDefault(this.defaultSort);
      this.serverSortBy = sort.serverSortBy;
      this.serverSortDesc = sort.serverSortDesc;
      this.reloadFromFirstPage();
    },
    selectedEmpresa() {
      this.reloadFromFirstPage();
    },
    selectedUnidades() {
      this.reloadFromFirstPage();
    },
    selectedAgencias() {
      this.reloadFromFirstPage();
    },
    selectedSlaFaixa() {
      this.reloadFromFirstPage();
    },
    selectedAreas() {
      this.reloadFromFirstPage();
    },
    dateStart() {
      this.reloadFromFirstPage();
    },
    dateEnd() {
      this.reloadFromFirstPage();
    },
    activeTab() {
      this.reloadFromFirstPage();
    },
  },
  methods: {

    applyViewFromRoute() {
      const q = (this.$route.query && this.$route.query.view) || '';
      const next = q === 'agendamento' ? 'agendamento' : 'status';
      if (this.viewMode === next) return;
      this._syncingViewFromRoute = true;
      this.viewMode = next;
      this.activeTab = 0;
      this.$nextTick(() => {
        this._syncingViewFromRoute = false;
        const sort = initSortFromDefault(this.defaultSort);
        this.serverSortBy = sort.serverSortBy;
        this.serverSortDesc = sort.serverSortDesc;
        this.reloadFromFirstPage();
      });
    },
    syncViewToRoute(mode) {
      const query = Object.assign({}, this.$route.query);
      if (mode === 'agendamento') {
        query.view = 'agendamento';
      } else {
        delete query.view;
      }
      if (JSON.stringify(query) === JSON.stringify(this.$route.query)) return;
      this.$router.replace({ path: this.$route.path, query }).catch(() => {});
    },
    initialize() {
      this.populaEmpresas();
      this.populaStatus();
    },
    populaStatus() {
      apiController.request(apiController.endpoints.getallStatus, {
        onSuccess: (response) => {
          const raw = Array.isArray(response.data) ? response.data : [];
          this.allStatusList = raw;
          this.statusList = filterTicketStatusesByPerfil(raw, 'colunas');
          this.reloadFromFirstPage();
        },
      });
    },
    /** Filtros de tela (não-coluna) enviados junto com os filtros da tabela. */
    serverTableBaseFilters() {
      const base = { modo: this.viewMode };
      if (this.viewMode === 'agendamento') {
        if (this.activeCategoriaAgendamento) {
          base.categoria_agendamento = this.activeCategoriaAgendamento;
        }
        // Período vai no request para filtrar só Finalizados (dt_finalizacao) nas contagens/lista;
        // a API não aplica período em abertos (atrasados/hoje/futuros/sem agendamento).
        if (this.dateStart || this.dateEnd) {
          base.periodo = { from: this.dateStart || null, to: this.dateEnd || null };
        }
      } else {
        if (this.activeStatusId != null) {
          base.id_status = this.activeStatusId;
        }
        if (this.dateStart || this.dateEnd) {
          base.periodo = { from: this.dateStart || null, to: this.dateEnd || null };
        }
      }
      if (this.selectedEmpresa) base.id_empresa = this.selectedEmpresa;
      if (this.selectedUnidades.length) base.id_unidades = this.selectedUnidades.slice();
      if (this.selectedAgencias.length) base.id_agencias = this.selectedAgencias.slice();
      if (this.selectedAreas.length) base.id_areas = this.selectedAreas.slice();
      if (this.selectedSlaFaixa) base.sla_faixa = this.selectedSlaFaixa;
      return base;
    },
    onFiltersUpdate(filters) {
      this.columnFilters = Object.assign({}, filters || {});
      const next = Object.assign(this.serverTableBaseFilters(), this.columnFilters);
      if (JSON.stringify(next) === JSON.stringify(this.searchFilters)) return;
      this.searchFilters = next;
      this.pageNumber = 1;
      this.serverTableLoad();
    },
    reloadFromFirstPage() {
      this.searchFilters = Object.assign(this.serverTableBaseFilters(), this.columnFilters);
      this.pageNumber = 1;
      this.serverTableLoad();
    },
    canLoadTable() {
      if (this.viewMode === 'agendamento') {
        return this.activeCategoriaAgendamento != null;
      }
      return this.activeStatusId != null;
    },
    serverTableLoad(options) {
      // Sem aba válida (status ainda não carregados) não consulta.
      if (!this.canLoadTable()) {
        this.tickets = [];
        this.totalTickets = 0;
        return;
      }
      const state = {
        pageNumber: this.pageNumber,
        numberOfRows: this.numberOfRows,
        serverSortBy: this.serverSortBy,
        serverSortDesc: this.serverSortDesc,
        searchFilters: this.searchFilters,
        lastItemsPerPageBeforeTodos: this.lastItemsPerPageBeforeTodos,
        total: this.totalTickets,
      };
      const { params, page, rowsPerPage, sortByArr, sortDescArr } = buildRequestParams(options, state);
      const requestId = (this._ticketsRequestId = (this._ticketsRequestId || 0) + 1);
      this.loadingtable = true;
      apiController.request(apiController.endpoints.getTicketsporstatus, {
        data: params,
        onSuccess: (response) => {
          // Troca rápida de aba/filtro: descarta respostas de requisições antigas.
          if (requestId !== this._ticketsRequestId) return;
          const data = response.data || {};
          this.tickets = Array.isArray(data.items) ? data.items : [];
          this.contagens = data.contagens || { porStatus: {}, finalizados: 0, agendamento: {} };
          const updates = computePageState(
            { items: this.tickets, total: Number(data.total) || 0 },
            { page, rowsPerPage, sortByArr, sortDescArr },
            state,
            this.itemsPerPageLastOption
          );
          this.totalTickets = updates.total;
          this.pageNumber = updates.pageNumber;
          this.numberOfRows = updates.numberOfRows;
          this.lastItemsPerPageBeforeTodos = updates.lastItemsPerPageBeforeTodos;
          this.serverSortBy = updates.serverSortBy;
          this.serverSortDesc = updates.serverSortDesc;
          this.loadingtable = false;
        },
        onError: () => {
          if (requestId !== this._ticketsRequestId) return;
          this.loadingtable = false;
        },
      });
    },
    loadItemsForExport(onProgress) {
      return fetchAllServerPagesForExport({
        fetchPage: async (params) => {
          const response = await apiController.request(
            apiController.endpoints.getTicketsporstatus,
            { data: params, silent: true },
          );
          return response && response.data;
        },
        state: {
          total: this.totalTickets,
          serverSortBy: this.serverSortBy,
          serverSortDesc: this.serverSortDesc,
          searchFilters: this.searchFilters,
        },
        onProgress,
      });
    },
    openTicketDetail(item) {
      this.selectedTicket = item;
      this.detailOpen = true;
    },
    countByStatus(idStatus) {
      return (this.contagens.porStatus && this.contagens.porStatus[idStatus]) || 0;
    },
    countByAgendamento(categoria) {
      const map = this.contagens.agendamento || {};
      return map[categoria] || 0;
    },
    isFinalized(ticket) {
      if (!ticket) return false;
      if (Number(ticket.finalizado) === 1) return true;
      const statusAtrib = ticket.id_status_atribuido != null ? Number(ticket.id_status_atribuido) : null;
      const statusDisp = ticket.id_status_dispatcher != null ? Number(ticket.id_status_dispatcher) : null;
      return STATUS_FECHADOS.includes(statusAtrib) || STATUS_FECHADOS.includes(statusDisp);
    },
    isOverdue(item) {
      if (!item || !item.dt_agendamento || this.isFinalized(item)) return false;
      const scheduled = parseLocalDate(item.dt_agendamento);
      if (!scheduled) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return scheduled.getTime() < today.getTime();
    },
    areaSelectText(item) {
      if (!item) return '';
      return [item.cod_area, item.nome_area].filter(Boolean).join(' - ');
    },
    unidadeSelectText(item) {
      if (!item) return '';
      const cod = item.cod_unidade != null ? String(item.cod_unidade).trim() : '';
      const desc = item.descricao_unidade != null ? String(item.descricao_unidade).trim() : '';
      if (cod && desc) return `${cod} — ${desc}`;
      return desc || cod || String(item.id_unidade != null ? item.id_unidade : '');
    },
    formatDateBr(isoDate) {
      if (!isoDate) return '';
      const [y, m, d] = isoDate.split('-');
      return `${d}/${m}/${y}`;
    },
    /** Nome do status como está cadastrado em status_tickets. */
    statusLabel(idStatus) {
      if (idStatus == null) return '';
      const config = this.statusConfig.find(c => c.id === idStatus);
      return config && config.nome ? config.nome : String(idStatus);
    },
    getStatusCardColor(idStatus) {
      if (idStatus === 1) return 'blue lighten-4';
      if (idStatus === 2) return 'orange lighten-4';
      if (idStatus === 3) return 'red lighten-4';
      if (idStatus === 4 || idStatus === 5) return 'green lighten-4';
      if (idStatus === 6) return 'grey lighten-3';
      if (idStatus === 7) return 'teal lighten-4';
      if (idStatus === 8) return 'grey lighten-2';
      return 'grey lighten-3';
    },
    populaEmpresas() {
      apiController.request(apiController.endpoints.getallEmpresas, {
        onSuccess: (response) => {
          if (response.data && Array.isArray(response.data)) {
            this.empresas = response.data.filter(empresa => empresa.ativa === 1);
          } else {
            this.empresas = [];
          }
        },
      });
    },
    getStatusColor(idStatus) {
      if (idStatus === 1) return 'blue';
      if (idStatus === 2) return 'orange';
      if (idStatus === 3) return 'red';
      if (idStatus === 4 || idStatus === 5) return 'green';
      if (idStatus === 6) return 'grey';
      if (idStatus === 7) return 'teal';
      if (idStatus === 8) return 'grey darken-1';
      return 'grey';
    },
  },
};
</script>

<style scoped>
.tabs-container {
  border-radius: 10px;
}
</style>
